/**
 * A14 follow-up — Review Queue Dashboard.
 * Pure UI wrapper around already-built endpoints (GET /review-queue,
 * PUT /{id}/fields, GET /audit-log) — zero new backend logic. Lets
 * admin/accounts confirm-as-correct or edit-then-confirm a record that was
 * auto-demoted to 'Verified - Under Review' by the AI-rule-enforcement
 * audit, instead of curling the API directly.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, Bot, CheckCircle2, History, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  extracted: 'Extraction Engine', ai: 'AI', ai_generated: 'AI Generated', ai_extracted: 'AI Extracted',
};

export default function ReviewQueue() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/historical-records/review-queue`)
      .then((r) => setRecords(r.data || []))
      .catch(() => toast.error('Failed to load review queue'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleResolved = (historicalId) => {
    setRecords((prev) => prev.filter((r) => r.historicalId !== historicalId));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="review-queue-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  const queueRecords = records;

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-6" data-testid="review-queue-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-amber-600" /> AI-Rule Review Queue
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Historical records where an AI/extraction-sourced field reached "Verified" without a human review —
          demoted to <code className="text-[11px] bg-slate-100 px-1 rounded">Verified - Under Review</code> until
          confirmed or corrected here. Nothing is ever auto-fixed.
        </p>
      </header>

      <Card className="p-4 flex items-center justify-between border border-slate-200" data-testid="review-queue-summary-card">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Records pending review</span>
        <span className="text-xl font-bold font-mono text-slate-900" data-testid="review-queue-count">{queueRecords.length}</span>
      </Card>

      {queueRecords.length === 0 ? (
        <Card className="p-12 text-center" data-testid="review-queue-empty">
          <CheckCircle2 className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
          <p className="text-slate-500">Nothing pending — every AI-sourced value that reached Verified has been human-reviewed.</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="review-queue-list">
          {queueRecords.map((rec) => (
            <ReviewCard key={rec.historicalId} record={rec} onResolved={() => handleResolved(rec.historicalId)} />
          ))}
        </div>
      )}
    </div>
  );
}

const ReviewCard = ({ record, onResolved }) => {
  const flags = record.fieldReviewFlags || [];
  const originalValues = {};
  flags.forEach((f) => { originalValues[f.fieldName] = record.fields?.[f.fieldName]?.value ?? ''; });

  const [values, setValues] = useState(originalValues);
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLog, setAuditLog] = useState(null);

  const submit = async (useOriginal) => {
    setSaving(true);
    try {
      const fieldUpdates = {};
      flags.forEach((f) => {
        fieldUpdates[f.fieldName] = { value: useOriginal ? originalValues[f.fieldName] : values[f.fieldName], source: 'human_verified' };
      });
      const reason = useOriginal ? 'Confirmed correct via Review Queue' : 'Edited and confirmed via Review Queue';
      const res = await axios.put(`${BACKEND_URL}/api/historical-records/${record.historicalId}/fields`, { fieldUpdates, reason });
      if (res.data?.needsFieldReview === false) {
        toast.success(`${record.crmDisplayNumber || record.historicalId} restored to '${res.data.verificationStatus}'`);
        onResolved();
      } else {
        toast.error('Some flagged fields are still unresolved');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resolve record');
    } finally {
      setSaving(false);
    }
  };

  const toggleAudit = () => {
    if (showAudit) { setShowAudit(false); return; }
    setShowAudit(true);
    if (!auditLog) {
      axios.get(`${BACKEND_URL}/api/historical-records/audit-log`, { params: { historicalId: record.historicalId } })
        .then((r) => setAuditLog(r.data || []))
        .catch(() => setAuditLog([]));
    }
  };

  const auditEntries = auditLog || [];
  const flagRows = flags;

  return (
    <Card className="p-5 border border-amber-100 bg-amber-50/30" data-testid={`review-card-${record.historicalId}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            {record.crmDisplayNumber || record.historicalId}
            <Badge className="bg-slate-100 text-slate-600 text-[10px] uppercase">{record.recordType}</Badge>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`review-card-status-${record.historicalId}`}>
            {record.financialYear || '—'} · status: <span className="font-mono">{record.verificationStatus}</span>
            {' '}(restores to <span className="font-mono">{record.preFlagVerificationStatus || 'Verified'}</span>)
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={toggleAudit} data-testid={`view-audit-${record.historicalId}`}>
          <History className="h-3.5 w-3.5 mr-1" />Audit trail
        </Button>
      </div>

      <div className="space-y-3">
        {flagRows.map((f) => {
          const entry = record.fields?.[f.fieldName] || {};
          return (
            <div key={f.fieldName} className="flex items-start gap-3 bg-white rounded-lg p-3 border border-slate-100"
              data-testid={`flag-row-${record.historicalId}-${f.fieldName}`}>
              <Bot className="h-4 w-4 text-amber-500 mt-1 shrink-0" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-700">{f.fieldName}</div>
                <div className="text-[11px] text-slate-400 mb-1.5">
                  Source: {SOURCE_LABELS[entry.source] || entry.source || '—'} · Confidence: {entry.confidence ?? '—'} · {f.reason}
                </div>
                <Input
                  data-testid={`flag-input-${record.historicalId}-${f.fieldName}`}
                  value={values[f.fieldName] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.fieldName]: e.target.value }))}
                  className="h-8 text-sm max-w-sm"
                />
              </div>
            </div>
          );
        })}
      </div>

      {showAudit && (
        <div className="mt-3 bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] font-mono max-h-40 overflow-y-auto"
          data-testid={`audit-log-${record.historicalId}`}>
          {auditEntries.length === 0 ? (
            <div className="text-slate-400">No audit entries yet.</div>
          ) : auditEntries.map((e, i) => (
            <div key={i} className="mb-1">[{e.occurredAt}] {e.attemptedAction} by {e.attemptedBy} — {e.reason}</div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-4">
        <Button size="sm" variant="outline" disabled={saving} onClick={() => submit(true)} data-testid={`confirm-correct-${record.historicalId}`}>
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
          Confirm as Correct
        </Button>
        <Button size="sm" disabled={saving} onClick={() => submit(false)} data-testid={`save-resolve-${record.historicalId}`}
          className="bg-teal-600 hover:bg-teal-700 text-white">
          Save Changes &amp; Resolve
        </Button>
      </div>
    </Card>
  );
};
